import React, { useState } from 'react'
import { Modal, Button, Icon, Tooltip } from 'antd'
import { T, fmtSec } from './AudioEditor/SharedLayout'

const MAX_SHOT_DURATION = 12
const MAX_DURATION_DIFF = 5
const asNum = value => {
  const n = Number.parseFloat(value)
  return Number.isFinite(n) ? n : 0
}

const SceneDurationFeedback = ({ scene }) => {
  const [modalVisible, setModalVisible] = useState(false)

  if (!scene || !asNum(scene.audio_duration)) return null

  const audioDuration = asNum(scene.audio_duration)
  const shots = scene.shots || []
  const totalShotDuration = shots.reduce(
    (acc, shot) => acc + asNum(shot.video_duration),
    0
  )
  const longShots = shots.filter(
    shot => asNum(shot.video_duration) > MAX_SHOT_DURATION
  )

  const hasLongShots = longShots.length > 0
  const durationDiff = Math.abs(totalShotDuration - audioDuration)
  const isSyncMismatch = durationDiff > MAX_DURATION_DIFF
  const isOver = totalShotDuration > audioDuration

  const needsMoreShots =
    audioDuration > MAX_SHOT_DURATION &&
    shots.length < Math.ceil(audioDuration / MAX_SHOT_DURATION)

  const getStatusContent = () => {
    if (hasLongShots) {
      return {
        color: T.orange,
        bg: T.orangeLight,
        border: T.orangeBorder,
        icon: 'warning',
        label: 'Sync Warning',
        tooltip: 'One or more shots exceed the recommended 12s limit',
        reason: `${longShots.length} shot${
          longShots.length > 1 ? 's are' : ' is'
        } over 12s. Shots this long can reduce AI video generation quality.`,
        fixLabel: 'Recommended action',
        fix: `Split the highlighted shot${
          longShots.length > 1 ? 's' : ''
        } into shorter segments. Aim for 4–10s per shot.`
      }
    }
    if (isSyncMismatch) {
      return {
        color: T.orange,
        bg: T.orangeLight,
        border: T.orangeBorder,
        icon: 'warning',
        label: 'Duration Warning',
        tooltip: `Shots are ${fmtSec(durationDiff)} ${
          isOver ? 'longer' : 'shorter'
        } than audio`,
        reason: isOver
          ? `Shots total ${fmtSec(totalShotDuration)}, audio is ${fmtSec(
              audioDuration
            )}. The final ${fmtSec(durationDiff)} of video will play silently.`
          : `Audio is ${fmtSec(audioDuration)}, shots only cover ${fmtSec(
              totalShotDuration
            )}. The final ${fmtSec(
              durationDiff
            )} of audio will not appear in the video.`,
        fixLabel: 'Recommended action',
        fix: isOver
          ? `Remove one or more shots to bring the total closer to ${fmtSec(
              audioDuration
            )}.`
          : `Add shots to cover the remaining ${fmtSec(
              durationDiff
            )}. At 6–8s per shot, that is roughly ${Math.ceil(
              durationDiff / 7
            )} more shot${Math.ceil(durationDiff / 7) > 1 ? 's' : ''}.`
      }
    }
    if (needsMoreShots) {
      return {
        color: T.blue,
        bg: T.blueLight,
        border: T.blueBorder,
        icon: 'info-circle',
        label: 'Sync Suggestion',
        tooltip: 'Adding more shots can improve sync across a long audio track',
        reason: `Audio is ${fmtSec(audioDuration)} with only ${
          shots.length
        } shot${
          shots.length !== 1 ? 's' : ''
        }. Fewer shots limits timing control.`,
        fixLabel: 'Suggestion',
        fix: `Add ${Math.ceil(audioDuration / MAX_SHOT_DURATION) -
          shots.length} more shot${
          Math.ceil(audioDuration / MAX_SHOT_DURATION) - shots.length > 1
            ? 's'
            : ''
        } to distribute pacing more evenly.`
      }
    }
    return {
      color: T.green,
      bg: T.greenLight,
      border: T.greenBorder,
      icon: 'check-circle',
      label: 'Perfect Sync',
      tooltip: 'Shot durations are well aligned with the audio',
      reason: `All ${shots.length} shots are under 12s and the total duration aligns with your audio.`,
      fixLabel: null,
      fix: null
    }
  }

  const status = getStatusContent()

  return (
    <>
      <Tooltip title={status.tooltip}>
        <div
          onClick={() => setModalVisible(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '4px 10px',
            background: status.bg,
            border: `1px solid ${status.border}`,
            borderRadius: 6,
            color: status.color,
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          className="sync-feedback-badge"
        >
          <Icon type={status.icon} />
          {status.label}
        </div>
      </Tooltip>

      <Modal
        maskClosable={false}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Icon type={status.icon} style={{ color: status.color }} />
            <span>Shot Sync Feedback</span>
          </div>
        }
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={[
          <Button
            key="close"
            type="primary"
            onClick={() => setModalVisible(false)}
          >
            Got it
          </Button>
        ]}
        centered
        width={520}
      >
        <div style={{ fontFamily: T.font }}>
          {/* Status block */}
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 12,
              padding: '12px 16px',
              background: status.bg,
              border: `1px solid ${status.border}`,
              borderRadius: 8,
              marginBottom: status.fix ? 10 : 16
            }}
          >
            <Icon
              type={status.icon}
              style={{ color: status.color, marginTop: 3 }}
            />
            <div>
              <div
                style={{
                  fontWeight: 700,
                  color: status.color,
                  marginBottom: 3,
                  fontSize: 13
                }}
              >
                {status.label}
              </div>
              <div style={{ fontSize: 13, color: T.inkMid, lineHeight: 1.55 }}>
                {status.reason}
              </div>
            </div>
          </div>

          {/* Recommended action block */}
          {status.fix && (
            <div
              style={{
                padding: '12px 16px',
                background: T.surface,
                border: `1px solid ${T.border}`,
                borderLeft: `3px solid ${T.blue}`,
                borderRadius: '0 8px 8px 0',
                marginBottom: 16
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  color: T.blue,
                  marginBottom: 5
                }}
              >
                {status.fixLabel}
              </div>
              <div style={{ fontSize: 13, color: T.inkMid, lineHeight: 1.6 }}>
                {status.fix}
              </div>
            </div>
          )}

          {/* Duration summary + shots breakdown */}
          <div
            style={{
              background: T.surface,
              padding: 20,
              borderRadius: 8,
              border: `1px solid ${T.border}`
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                paddingBottom: 12,
                borderBottom: `1px solid ${T.borderLight}`
              }}
            >
              <div>
                <div
                  style={{
                    color: T.inkSub,
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: 'uppercase'
                  }}
                >
                  Scene Audio
                </div>
                <div style={{ color: T.ink, fontWeight: 700, fontSize: 16 }}>
                  {fmtSec(audioDuration)}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div
                  style={{
                    color: T.inkSub,
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: 'uppercase'
                  }}
                >
                  Shots Duration Total
                </div>
                <div
                  style={{
                    color: isSyncMismatch ? status.color : T.ink,
                    fontWeight: 700,
                    fontSize: 16
                  }}
                >
                  {fmtSec(totalShotDuration)}
                </div>
              </div>
            </div>

            <div style={{ marginTop: 16 }}>
              <div
                style={{
                  color: T.inkSub,
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  marginBottom: 10,
                  display: 'flex',
                  justifyContent: 'space-between'
                }}
              >
                <span>Shots Breakdown ({shots.length})</span>
                <span>Duration</span>
              </div>

              <div
                style={{
                  maxHeight: '200px',
                  overflowY: 'auto',
                  paddingRight: 4
                }}
              >
                {shots.map((s, idx) => {
                  const shotDuration = asNum(s.video_duration)
                  const isLong = shotDuration > MAX_SHOT_DURATION
                  return (
                    <div
                      key={s.id || idx}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        fontSize: 13,
                        padding: '8px 12px',
                        background: isLong ? T.redLight : 'transparent',
                        borderRadius: 6,
                        marginBottom: 4,
                        border: isLong
                          ? `1px solid ${T.redBorder}`
                          : '1px solid transparent'
                      }}
                    >
                      <span style={{ color: T.inkMid }}>
                        <Icon
                          type="video-camera"
                          style={{ marginRight: 8, opacity: 0.5 }}
                        />
                        Shot {idx + 1}
                        {isLong && (
                          <span
                            style={{
                              marginLeft: 8,
                              fontSize: 10,
                              fontWeight: 700,
                              color: T.orange,
                              border: `1px solid ${T.orangeBorder}`,
                              borderRadius: 3,
                              padding: '1px 5px'
                            }}
                          >
                            Split
                          </span>
                        )}
                      </span>
                      <span
                        style={{
                          color: isLong ? T.orange : T.ink,
                          fontWeight: isLong ? 700 : 500,
                          fontVariantNumeric: 'tabular-nums'
                        }}
                      >
                        {fmtSec(shotDuration)}
                      </span>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </>
  )
}

export default SceneDurationFeedback
